"use client";

import { Component, ReactNode, type ErrorInfo } from "react";
import { AlertCircle } from "lucide-react";
import { Button } from "../ui/button";

interface Props {
  children: ReactNode;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error Boundary Caught:", error, errorInfo);
  }

  reset = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return <DisplayError onReset={this.reset} />;
    }

    return this.props.children;
  }
}

export function DisplayError({ onReset }: { onReset?: () => void }) {
  const handleReset = () => {
    onReset?.();
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-destructive" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">
          Something went wrong...
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          We encountered an error loading the page. Please try refreshing the
          page.
        </p>
        <Button
          onClick={handleReset}
          className="bg-secondary text-secondary-foreground"
        >
          Try Again
        </Button>
      </div>
    </div>
  );
}
